import React from 'react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div
              className="flex items-center space-x-2 cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_ec527428-3aec-42a4-a1f7-d2e66c0be0df/artifacts/hktez0bk_logo_transs.png"
                alt="doorush logo"
                className="h-10 w-auto"
              />
            </div>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-2 border-[#007e7a] text-[#007e7a] hover:bg-[#007e7a] hover:text-white"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto prose prose-lg text-gray-700 leading-relaxed">

          <h1 className="text-4xl font-bold text-gray-900">
            Refund Policy
          </h1>

          <p className="text-gray-600">
            Last Updated: 26/06/2025
          </p>

          <p>
            This Refund Policy ("Policy") comprehensively outlines the terms and
            conditions governing the refund of service fees paid through the
            doorush mobile application and website ("Platform"), owned and
            operated by Holbewoner Technologies Private Limited ("doorush,"
            "Company," "we," "us," or "our"). This Policy has been drafted in
            accordance with prevailing laws of India, including but not limited
            to the Consumer Protection Act, 2019, Information Technology Act,
            2000, The Indian Contract Act, 1872, and the Digital Personal Data
            Protection Act, 2023 ("DPDP Act").
          </p>

          <p>
            By accessing and using the Platform and transacting for services,
            you confirm that you have read, understood, and agreed to be bound
            by this Refund Policy. This Policy is intended to promote
            transparency, consistency, and accountability in the resolution of
            refund-related matters. It ensures that our processes meet the
            standards of fairness, integrity, and legal compliance, providing
            our customers with confidence and trust in using our Platform.
          </p>

          <h2>1. Scope and General Conditions</h2>

          <p>
            <strong>1.1</strong> This Refund Policy applies to all transactions
            initiated by users ("Customers") through the Platform using any
            supported payment method, including UPI, credit/debit cards, net
            banking, mobile wallets, or doorush Wallet credits.
          </p>

          <p>
            <strong>1.2</strong> Services booked via the Platform are deemed
            confirmed upon successful payment. Bookings are treated as
            time-bound service contracts under the Indian Contract Act, and any
            refund shall be governed strictly under the terms of this policy.
          </p>

          <p>
            <strong>1.3</strong> The refund process begins only after a formal
            request is raised by the customer and evaluated by our Quality
            Control Team. The Company reserves the right to verify customer
            claims through order history, timestamps, GPS logs, internal
            communication records, and service partner reports.
          </p>

          <p>
            <strong>1.4</strong> All customers are strongly advised to verify
            service details, location accuracy, service type, and time slot
            before booking. Inaccuracies caused by user error may render a
            refund request invalid.
          </p>

          <p>
            <strong>1.5</strong> Fraudulent refund requests, misrepresentation,
            or abuse of the refund system may result in account suspension and
            legal prosecution under Sections 417 and 420 of the Indian Penal
            Code (IPC), 1860.
          </p>

          <h2>2. Conditions Eligible for Refund</h2>

          <p>Refunds may be granted under the following validated conditions:</p>

          <p>
            <strong>2.1 Service Provider Absence:</strong> If the assigned
            provider fails to arrive within 30 minutes beyond the confirmed
            slot and a substitute cannot be arranged, customers are entitled to
            a full refund.
          </p>

          <p>
            <strong>2.2 Company-Initiated Cancellation:</strong> If doorush
            cancels a service due to technical faults, health emergencies, lack
            of partner availability, or other internal reasons, the customer
            may request either a refund or service rescheduling.
          </p>

          <p>
            <strong>2.3 Excess Payment/Duplicate Charges:</strong> If double
            payment or overbilling is identified through transaction logs or
            customer bank records, doorush will process the excess refund
            without requiring further requests.
          </p>

          <p>
            <strong>2.4 Unprofessional Conduct or Service Failure:</strong> If
            the partner behaves inappropriately or fails to complete the
            service as agreed, and the issue is verified with evidence (e.g.,
            photos, timestamped chats), a refund may be granted.
          </p>

          <p>
            <strong>2.5 Delay Beyond 30 Minutes:</strong> If the partner reaches
            more than 30 minutes late and the user refuses service, the
            customer may claim a refund.
          </p>

          <p>
            <strong>2.6 Incorrect Service Delivered:</strong> If the customer
            receives a service other than what was booked, or critical tasks
            were missed, a refund may be provided following an audit.
          </p>

          <p>
            <strong>2.7 Incapacitation or Emergency:</strong> In rare events
            where the user is unable to avail the service due to a medical or
            other emergency (with supporting proof), doorush may offer a refund
            or reschedule at its discretion.
          </p>

          <h2>3. Non-Refundable Scenarios</h2>

          <ul>
            <li>Cancellations due to user preference without valid reason or documentation</li>
            <li>Wrong address or phone number submitted by the customer</li>
            <li>Refusal of service after provider arrival with no fault on doorush’s part</li>
            <li>Booking completed using discounts, vouchers, or credits, unless non-service is proven</li>
            <li>Force majeure events such as flood, strikes, riots, or state-imposed curfews</li>
            <li>Delays that are within a tolerable range (under 30 minutes)</li>
            <li>Dissatisfaction based purely on personal preferences without service quality breach</li>
          </ul>

          <h2>4. Refund Request Process</h2>

          <p>
            <strong>4.1</strong> The request must be initiated within 3 hours of
            the end of the service slot or the occurrence of the issue.
          </p>

          <p><strong>4.2</strong> Refund requests must contain:</p>

          <ul>
            <li>Booking ID and registered contact number</li>
            <li>Date and time of the service</li>
            <li>Reason for the request</li>
            <li>Photos, videos, or relevant proof (if applicable)</li>
          </ul>

          <p><strong>4.3</strong> Request channels:</p>

          <ul>
            <li>In-app customer support chat</li>
            <li>Email: support@doorush.in</li>
            <li>Customer helpline: +91 9092963743</li>
          </ul>

          <p>
            <strong>4.4</strong> Customers must cooperate in good faith.
            Failure to respond or misrepresentation may lead to
            disqualification of the claim.
          </p>

          <h2>5. Review, Approval, and Payment Timelines</h2>

          <p>
            <strong>5.1</strong> Every claim is reviewed within 3 working days
            by our internal quality team.
          </p>

          <p>
            <strong>5.2</strong> Once approved, the refund is processed within
            7 working days through the original payment method or doorush
            Wallet, as per customer preference.
          </p>

          <p>
            <strong>5.3</strong> If the refund involves a third-party payment
            gateway, doorush is not liable for delays beyond its control but
            will provide all necessary assistance.
          </p>

          <p>
            <strong>5.4</strong> For UPI, wallet, or card refunds, the time taken
            for funds to reflect depends on the policies of the respective
            financial institutions.
          </p>

          <h2>6. Partial Refunds and Alternate Compensation</h2>

          <p>
            <strong>6.1</strong> If only part of the service was completed or
            issue severity is partial, a proportional refund may be approved.
          </p>

          <p>
            <strong>6.2</strong> Refunds can also be credited to the doorush
            Wallet for faster settlement. Wallet refunds are valid for 6
            months.
          </p>

          <p>
            <strong>6.3</strong> At its discretion, doorush may also offer:
          </p>

          <ul>
            <li>Free service re-attempts</li>
            <li>Discount coupons for future use</li>
            <li>Loyalty credits</li>
          </ul>

          <h2>7. Compliance, Enforcement, and Jurisdiction</h2>

          <p>
            <strong>7.1</strong> This Policy is aligned with the Consumer
            Protection (E-Commerce) Rules, 2020 and is enforceable as per Indian
            legal standards.
          </p>

          <p>
            <strong>7.2</strong> Data collected in the refund process is
            securely stored and used in accordance with our Privacy Policy,
            under compliance with the DPDP Act, 2023.
          </p>

          <p>
            <strong>7.3</strong> This Policy forms part of the Terms of Use and
            is legally binding under the Indian Contract Act, 1872.
          </p>

          <p>
            <strong>7.4</strong> Any dispute arising from the refund process
            shall be subject to the exclusive jurisdiction of courts in
            Chennai, Tamil Nadu.
          </p>

          <h2>8. Updates and Final Decision</h2>

          <p>
            <strong>8.1</strong> doorush reserves the right to modify, revise,
            or suspend this Refund Policy at any time.
          </p>

          <p>
            <strong>8.2</strong> All changes will be posted on the Platform and
            shall be effective immediately upon publication.
          </p>

          <p>
            <strong>8.3</strong> In all refund-related matters, the final
            decision rests with the Company, unless legally overruled.
          </p>

          <p className="font-semibold mt-6">
            By continuing to use doorush, you agree to this Refund Policy and
            the terms contained herein.
          </p>

        </div>
      </section>
    </div>
  );
};

export default RefundPolicy;
